import classnames from 'classnames';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { THUMBNAIL_SKELETON_DISPLAY_LIMIT } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import useArtifactAreasThumbnail from './useArtifactAreasThumbnail';

export type ArtifactAreasThumbnailProps = {
  timezoneOffset: number;
  profileId: string;
  studyId: string;
  startReport: any;
  stopReport: any;
};

/**
 * Day strip letting the user jump between recorded days in the artifact areas view. Renders skeleton placeholders while the daily counts load, then one button per day showing the formatted date, its ordinal and its artifact count, highlighting the currently selected date.
 *
 * @param props - The thumbnail props, including timezoneOffset used to format each day label.
 * @returns The rendered day thumbnail row, or its skeleton placeholders while loading.
 */
const ArtifactAreasThumbnail = ({
  timezoneOffset,
  profileId,
  studyId,
  startReport,
  stopReport,
}: ArtifactAreasThumbnailProps) => {
  const { isLoadingThumbnail, dailyCount, selectedDateValue, onClickThumbnail } =
    useArtifactAreasThumbnail({ timezoneOffset, profileId, studyId, startReport, stopReport });

  if (isLoadingThumbnail) {
    return (
      <div className="artifact-areas-thumbnail">
        <div className="thumbnail-content">
          {_.map(_.range(THUMBNAIL_SKELETON_DISPLAY_LIMIT), () => (
            <div className="summary-thumbnail-item__skeleton">
              <Skeleton height={60} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div id="date-thumbnail" className="artifact-areas-thumbnail">
      <div className="thumbnail-content">
        {_.map(dailyCount, (x, i) => (
          <button
            id={`artifact-areas-thumbnail-item-${i}`}
            className={classnames(
              'summary-thumbnail-item',
              x.date === selectedDateValue ? '--active' : '',
            )}
            onClick={() => {
              onClickThumbnail(x);
            }}
          >
            <div className="date-row">
              <div>{convertToMomentJs(x.date, timezoneOffset).format('MM/DD')}</div>
              <div>{`(${i + 1})`}</div>
            </div>
            <div className="artifact-row">
              <div>Artifact:</div>
              <div>{x.count >= 0 ? x.count : 0}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ArtifactAreasThumbnail;
